import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FaWifi, FaRedo } from "react-icons/fa";
import useSelectedDomain from "../../service/Hooks/useSelectedDomain";

import {
  getVolumeTransaction,
  getVolumeTransactionPublic,
} from "../../service/api/TransactionService";

import TransactionTables from "./TransactionTable/TransactionTables";
import { formatAmountInThousands } from "../../service/utils/formatters";
import MouvementFinancierTable from "../../AccountHistorics/MouvementFinancierTable";
import PrivateTransactionsModal from "./Modal/PrivateTransactionsModal";
import { getPrivateTransactionsSummary } from "../../service/api/TransfertTitre/PrivateTransactionService";
import StatCardsSkeleton from "../../skeleton/StatCardsSkeleton";

/**
 * Carte de statistique avec gestion offline
 */
const StatCard = ({
  title,
  count,
  loading,
  icon,
  backgroundColor,
  badgeColor,
  isOffline,
  onRetry,
  isCurrency = false,
  onClick,
  clickable = false,
  privateSummary = null,
}) => {
  const displayValue =
    isOffline && !loading
      ? "--"
      : loading
      ? null
      : isCurrency
      ? formatAmountInThousands(count)
      : (count ?? 0).toLocaleString("fr-FR");

  return (
    <div className="col-6 col-lg-3">
      <div
        className={`card counter h-100 border-0 shadow-sm position-relative overflow-hidden ${
          isOffline ? "card-offline" : ""
        } ${clickable && !isOffline ? "clickable" : ""}`}
        style={{
          backgroundColor: backgroundColor,
          transition: "all 0.2s ease",
          cursor: clickable && !isOffline ? "pointer" : "default",
          borderRadius: "10px",
          minHeight: "140px",
          opacity: isOffline ? 0.85 : 1,
        }}
        onClick={!isOffline ? onClick : undefined}
      >
        {/* Indicateur offline */}
        {isOffline && (
          <div className="offline-indicator-card">
            <FaWifi size={10} />
            <span>Hors ligne</span>
          </div>
        )}

        {/* Bouton retry si offline */}
        {isOffline && !loading && (
          <button
            className="btn-retry-card"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
          >
            <FaRedo size={10} />
          </button>
        )}

        <div className="card-body text-white p-3 position-relative">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div
              className="card-box-icon"
              style={{
                background: "rgba(255,255,255,0.15)",
                borderRadius: "8px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isOffline ? 0.7 : 1,
              }}
            >
              {icon}
            </div>

            <span
              className={`badge ${badgeColor} border-0 px-2 py-1`}
              style={{
                backgroundColor: isOffline
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(255,255,255,0.25)",
                fontSize: "0.65rem",
                fontWeight: "600",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {loading ? "..." : title.split(" ")[0]}
            </span>
          </div>

          <div className="chart-num">
            <p
              className="mb-1 text-white small fw-medium text-uppercase"
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.5px",
                opacity: isOffline ? 0.6 : 0.85,
                lineHeight: "1.2",
              }}
            >
              {title}
            </p>

            {loading ? (
              <div className="d-flex align-items-center" style={{ height: "28px" }}>
                <div
                  className="spinner-border spinner-border-sm text-white me-2"
                  style={{ width: "12px", height: "12px" }}
                  role="status"
                ></div>
                <span className="small text-white-50">...</span>
              </div>
            ) : (
              <h4
                className="mb-0 fw-bold text-white"
                style={{
                  fontSize: "1.25rem",
                  letterSpacing: "-0.5px",
                  lineHeight: "1.2",
                  opacity: isOffline ? 0.6 : 1,
                }}
              >
                {displayValue}
              </h4>
            )}
          </div>

          {/* Mini résumé privé */}
          {privateSummary && !loading && !isOffline && (
            <div
              className="mt-2 pt-2"
              style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
            >
              <small
                className="text-white d-block"
                style={{ opacity: "0.8", fontSize: "0.65rem" }}
              >
                {privateSummary.uniqueSecurities} titres •{" "}
                {formatAmountInThousands(privateSummary.totalValue)}
              </small>
            </div>
          )}

          {/* Texte offline */}
          {isOffline && !loading && (
            <div
              className="mt-2 pt-2"
              style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
            >
              <small
                className="text-white d-block"
                style={{ opacity: "0.6", fontSize: "0.65rem" }}
              >
                Données temporairement indisponibles
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TransactionsList = ({ onOpenModal }) => {
  const selectedDomain = useSelectedDomain();

  const [countTotal, setCountTotal] = useState(0);
  const [countPrivate, setCountPrivate] = useState(0);
  const [countPublic, setCountPublic] = useState(0);
  const [volumeTotal, setVolumeTotal] = useState(0);

  const [loading, setLoading] = useState({
    total: true,
    private: true,
    public: true,
    volume: true,
  });

  const [isOffline, setIsOffline] = useState(false);
  const [showPrivateModal, setShowPrivateModal] = useState(false);
  const [privateSummary, setPrivateSummary] = useState(null);

  // Vérifier si toutes les cartes sont en chargement
  const isFullyLoading = Object.values(loading).every(Boolean);

  // Détection erreur connexion
  const isConnectionError = (error) => {
    if (!error) return false;
    return (
      !error.response ||
      error.code === "ECONNABORTED" ||
      error.message?.includes("Network Error") ||
      error.message?.includes("connexion")
    );
  };

  /**
   * Total = privé + public
   * (hook au top level => correct React)
   */
  useEffect(() => {
    setCountTotal((countPrivate ?? 0) + (countPublic ?? 0));
  }, [countPrivate, countPublic]);

  /**
   * Fetch principal
   */
  const fetchData = useCallback(async () => {
    if (!selectedDomain?.id) return;

    setLoading({ total: true, private: true, public: true, volume: true });
    setIsOffline(false);

    let offlineDetected = false;

    const loadData = async (apiCall, onSuccess, key) => {
      try {
        const data = await apiCall(selectedDomain.id);
        onSuccess(data);
      } catch (error) {
        console.error(`Erreur ${key}:`, error);
        if (isConnectionError(error)) {
          offlineDetected = true;
          setIsOffline(true);
        }
      } finally {
        setLoading((prev) => ({ ...prev, [key]: false }));
      }
    };

    await Promise.all([
      // VOLUME
      loadData(
        getVolumeTransaction,
        (data) => {
          setVolumeTotal(data?.volume ?? 0);
        },
        "volume"
      ),

      // PRIVE
      loadData(
        getPrivateTransactionsSummary,
        (response) => {
          if (response?.success && response?.data) {
            setCountPrivate(response.data.totalTransactions ?? 0);
            setPrivateSummary(response.data);
          } else {
            setCountPrivate(0);
            setPrivateSummary(null);
          }
        },
        "private"
      ),

      // PUBLIC
      loadData(
        getVolumeTransactionPublic,
        (data) => {
          setCountPublic(data?.count ?? 0);
        },
        "public"
      ),
    ]);

    // total loading terminé (le calcul se fait par useEffect)
    setLoading((prev) => ({ ...prev, total: false }));

    if (offlineDetected) {
      toast.error("Problème de connexion - Données partiellement chargées", {
        duration: 4000,
      });
    }
  }, [selectedDomain]);

  /**
   * Chargement initial + listener online
   */
  useEffect(() => {
    fetchData();

    const handleOnline = () => {
      if (isOffline) {
        toast.success("Connexion rétablie");
        fetchData();
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [fetchData, isOffline]);

  const handlePrivateCardClick = () => {
    if (isOffline) {
      toast.error("Détails indisponibles hors ligne");
      return;
    }

    if (!loading.private && countPrivate > 0) {
      setShowPrivateModal(true);
    } else {
      toast.info("Aucune donnée de transaction privée disponible");
    }
  };

  /**
   * IMPORTANT :
   * - on utilise id (pas key)
   * - on passe key={id} dans le map
   */
  const cardData = [
    {
      id: "private",
      title: "Transactions Privées",
      count: countPrivate,
      loading: loading.private,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      ),
      backgroundColor: "#e67e22",
      badgeColor: "bg-warning",
      onClick: handlePrivateCardClick,
      clickable: true,
    },
    {
      id: "public",
      title: "Transactions Publiques",
      count: countPublic,
      loading: loading.public,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4 8 4v14" />
          <path d="M16 21V9.5" />
          <path d="M8 21V9.5" />
        </svg>
      ),
      backgroundColor: "#27ae60",
      badgeColor: "bg-success",
    },
    {
      id: "volume",
      title: "Volume Boursier",
      count: volumeTotal,
      loading: loading.volume,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="20" x2="12" y2="10"></line>
          <line x1="18" y1="20" x2="18" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="16"></line>
        </svg>
      ),
      backgroundColor: "#8e44ad",
      badgeColor: "bg-purple",
      isCurrency: true,
    },
    {
      id: "total",
      title: "Total",
      count: countTotal,
      loading: loading.total,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      ),
      backgroundColor: "#c0392b",
      badgeColor: "bg-danger",
    },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <>
      <div className="col-xl-12">
        <div className="page-titles" style={{ marginBottom: "24px" }}>
          <nav className="breadcrumb-modern" aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <span className="breadcrumb-main text-uppercase letter-spacing">
                  Transactions
                </span>
                <span className="breadcrumb-year-badge">{currentYear}</span>

                {isOffline && (
                  <span className="offline-badge-header ms-2">
                    <FaWifi size={10} />
                    Hors ligne
                  </span>
                )}
              </li>

              <li className="breadcrumb-item active" aria-current="page">
                <span className="breadcrumb-current">Tableau de bord</span>
              </li>
            </ol>
          </nav>

          <div className="page-actions">
            <button
              className="btn btn-primary btn-new-order"
              onClick={onOpenModal}
              style={{
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span className="btn-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </span>
              <span className="btn-text">Nouvelle transaction</span>
            </button>
          </div>
        </div>

        {/* Skeleton si chargement complet */}
        {isFullyLoading ? (
          <StatCardsSkeleton />
        ) : (
          <div className="row g-3 mb-4">
            {cardData.map(({ id, ...card }) => (
              <StatCard
                key={id}
                {...card}
                isOffline={isOffline}
                onRetry={fetchData}
                privateSummary={id === "private" ? privateSummary : null}
              />
            ))}
          </div>
        )}
      </div>

      <TransactionTables />

      <div className="mt-5">
        <MouvementFinancierTable />
      </div>

      <PrivateTransactionsModal
        show={showPrivateModal}
        onClose={() => setShowPrivateModal(false)}
      />
    </>
  );
};

export default TransactionsList;
